<template>
  <div
    v-if="appStore.isOffline"
    class="fixed inset-0 z-[9999] flex flex-col bg-slate-50 dark:bg-slate-950"
  >
    <div class="flex justify-end p-4">
      <LanguageSelection />
    </div>
    <div class="flex flex-1 items-center justify-center p-4">
      <div class="max-w-md text-center">
        <div
          class="mx-auto mb-6 flex h-24 w-24 items-center justify-center rounded-full bg-red-100 text-red-600"
        >
          <ExclamationTriangleIcon class="h-12 w-12" />
        </div>
        <h2 class="mb-2 text-2xl font-bold text-slate-900 dark:text-slate-50">
          {{ $t('error.offlineTitle') }}
        </h2>
        <p class="mb-6 text-slate-500 dark:text-slate-400">
          {{ $t('error.offlineMessage') }}
        </p>
        <button
          @click="reloadPage"
          class="inline-flex items-center justify-center rounded-lg bg-primary-600 px-6 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
        >
          {{ $t('error.retry') }}
        </button>
      </div>
    </div>
  </div>
  <router-view v-else />
  <ToastStack :items="appStore.toasts" />
</template>

<script setup lang="ts">
import { onMounted, onUnmounted } from 'vue';
import { ExclamationTriangleIcon } from '@heroicons/vue/24/outline';
import { useAppStore } from '@/stores/app';
import LanguageSelection from '@/components/LanguageSelection.vue';
import ToastStack from '@/components/admin/ToastStack.vue';
import { useDarkMode } from '@/composables/useDarkMode';

const appStore = useAppStore();
useDarkMode(); // Initialize dark mode globally on app mount

onMounted(() => {
  // Check if we are running inside an iframe
  if (window.self !== window.top) {
    document.documentElement.classList.add('is-iframe');

    const sendHeight = () => {
      const appEl = document.getElementById('app');
      // Measure offsetHeight of the app root container which collapses correctly,
      // falling back to body offsetHeight or documentElement scrollHeight.
      const height =
        appEl && appEl.offsetHeight > 0
          ? appEl.offsetHeight
          : document.body.offsetHeight || document.documentElement.scrollHeight;
      window.parent.postMessage({ type: 'resize-booking-iframe', height }, '*');
    };

    // Send initial height
    sendHeight();

    // Attach ResizeObserver to body to monitor any dynamic height changes
    const resizeObserver = new ResizeObserver(() => {
      sendHeight();
    });
    resizeObserver.observe(document.body);

    onUnmounted(() => {
      resizeObserver.disconnect();
      document.documentElement.classList.remove('is-iframe');
    });
  }
});

function reloadPage() {
  window.location.reload();
}
</script>

<style>
/* Adjust height constraints and clear backgrounds when embedded in an iframe */
html.is-iframe,
html.is-iframe body,
html.is-iframe #app,
html.is-iframe .min-h-screen,
html.is-iframe .network-layout,
html.is-iframe .network-layout main,
html.is-iframe .magazin-layout,
html.is-iframe .magazin-layout main,
html.is-iframe .user-layout,
html.is-iframe .user-layout main {
  min-height: auto !important;
  height: auto !important;
  background-color: transparent !important;
  background: transparent !important;
}

/* Hide header in all layouts (User, Magazin, Network) when in iframe mode */
html.is-iframe header {
  display: none !important;
}

/* Remove outer padding so the content aligns cleanly with the parent page margins */
html.is-iframe .mx-auto.w-full.max-w-7xl.px-4.py-8 {
  padding: 0 !important;
}

/* On mobile (below 768px) in iframe, keep enough bottom padding for the fixed footer to prevent overlap */
@media (max-width: 767px) {
  html.is-iframe .space-y-6.pb-24 {
    padding-bottom: 96px !important;
  }
}

/* On desktop (768px and up) in iframe, remove bottom padding since the footer is inline/static */
@media (min-width: 768px) {
  html.is-iframe .space-y-6.pb-24 {
    padding-bottom: 1rem !important;
  }
}
</style>
